import React, { useState, useEffect } from "react";
import { fetchWeatherDataAPI } from './api';
import { useBBQScore } from './hooks/useBBQScore';
import LocationInput from './components/LocationInput';
import WeatherDisplay from './components/WeatherDisplay';

const CSS = `
  .gong-app {
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 16px 16px;
    position: relative;
    background:
      repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.011) 2px, rgba(255,255,255,0.011) 4px),
      repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.007) 3px, rgba(255,255,255,0.007) 6px),
      #191910;
  }
  .gong-app::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
  .gong-board {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 700px;
    box-sizing: border-box;
    align-self: center;
    border: 2px solid rgba(240,236,224,0.16);
    border-radius: 3px;
    padding: 20px 36px 16px;
    background: radial-gradient(ellipse at 40% 0%, rgba(255,255,255,0.02) 0%, transparent 55%), #1e1e17;
    box-shadow: 0 0 0 1px rgba(240,236,224,0.05), 0 24px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04);
  }
  .gong-board::before { content: '✦'; position: absolute; top: 13px; left: 18px; color: rgba(240,236,224,0.22); font-size: 16px; }
  .gong-board::after  { content: '✦'; position: absolute; top: 13px; right: 18px; color: rgba(240,236,224,0.22); font-size: 16px; }
  .gong-header { text-align: center; margin-bottom: 12px; }
  .gong-title-rule { display: flex; align-items: center; gap: 14px; margin-bottom: 8px; }
  .gong-title-rule::before, .gong-title-rule::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(240,236,224,0.18), transparent);
  }
  .gong-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(32px, 6vw, 48px);
    color: #f0ece0;
    letter-spacing: 0.08em;
    line-height: 1;
    text-shadow: 2px 2px 0 rgba(0,0,0,0.5);
    margin: 0;
  }
  .gong-subtitle {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: rgba(240,236,224,0.22);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    margin-top: 3px;
  }
  .gong-loading {
    display: flex; justify-content: center; padding: 10px 0;
  }
  .gong-dots span {
    display: inline-block; width: 5px; height: 5px;
    background: rgba(240,236,224,0.55); border-radius: 50%; margin: 0 3px;
    animation: gongDot 1.2s ease-in-out infinite;
  }
  .gong-dots span:nth-child(2) { animation-delay: 0.2s; }
  .gong-dots span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes gongDot {
    0%,80%,100% { opacity: 0.2; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1.1); }
  }
  .gong-error {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px; color: #eb5757;
    background: rgba(235,87,87,0.08);
    border: 1px solid rgba(235,87,87,0.2);
    border-radius: 2px;
    padding: 8px 12px; margin: 8px 0 16px;
    text-align: center;
  }
  .gong-footer {
    display: flex; justify-content: space-between; align-items: center;
    border-top: 1px solid rgba(240,236,224,0.07); padding-top: 8px; margin-top: 2px;
  }
  .gong-footer-text {
    font-family: 'DM Mono', monospace; font-size: 10px;
    color: rgba(240,236,224,0.3); letter-spacing: 0.08em;
  }
  .gong-footer-text a { color: rgba(240,236,224,0.3); text-decoration: underline; }
  .gong-footer-text a:hover { color: rgba(240,236,224,0.6); }
  @media (max-width: 540px) {
    .gong-board { padding: 14px 16px 12px; }
  }
`;

export default function BBQWeatherIndex() {
  const [location, setLocation] = useState("");
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unit, setUnit] = useState('celsius');
  const [currentBBQScore, setCurrentBBQScore] = useState(0);

  const { bbqScore } = useBBQScore(weather);

  useEffect(() => {
    if (weather && !isLoading) setCurrentBBQScore(bbqScore);
    else if (!weather) setCurrentBBQScore(0);
  }, [bbqScore, weather, isLoading]);

  const handleUnitChange = (event, newUnit) => { if (newUnit !== null) setUnit(newUnit); };

  const handleLocationSelected = (selectedLocation) => {
    if (selectedLocation && typeof selectedLocation === 'object') {
      setLocation('');
      fetchWeather(selectedLocation);
    } else {
      setLocation(selectedLocation);
      fetchWeather(selectedLocation);
    }
  };

  const fetchWeather = async (loc) => {
    if (!loc) return;
    setError(""); setWeather(null); setForecast(null); setIsLoading(true);
    try {
      const data = await fetchWeatherDataAPI(loc);
      setWeather(data);
      setForecast(data.forecast || null);
    } catch (err) {
      setError(err.message || "Failed to fetch weather. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="gong-app">
        <div className="gong-board">

          <div className="gong-header">
            <div className="gong-title-rule">
              <span style={{fontSize:20}}>🔥</span>
              <h1 className="gong-title">GRILL OR NO GRILL</h1>
              <span style={{fontSize:20}}>🔥</span>
            </div>
            <p className="gong-subtitle">— BBQ weather forecast for your location —</p>
          </div>

          <LocationInput onLocationSubmit={handleLocationSelected} initialLocation={location} />

          {isLoading && (
            <div className="gong-loading">
              <div className="gong-dots"><span/><span/><span/></div>
            </div>
          )}

          {error && !isLoading && (
            <div className="gong-error">{error}</div>
          )}

          {weather && !error && !isLoading && (
            <WeatherDisplay
              weatherData={weather}
              bbqScore={currentBBQScore}
              unit={unit}
              onUnitChange={handleUnitChange}
              forecast={forecast}
            />
          )}

          <div className="gong-footer">
            <span className="gong-footer-text">
              ⚑ {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
            </span>
            <span className="gong-footer-text">
              Weather by <a href="https://openweathermap.org/" target="_blank" rel="noopener noreferrer">OpenWeather</a>
            </span>
          </div>

        </div>
      </div>
    </>
  );
}
